import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import type { Event, Graph, WithContext } from 'schema-dts'
import type { SearchEngineEventItem } from '~/composables/types/searchEngine'

const useSearchEngineEventSchema = (
  events: SearchEngineEventItem[],
  overwrites: any = undefined
): any => {
  const schema = ref<Graph | undefined>(undefined)
  const script = computed(() => ({
    type: 'application/ld+json',
    innerHTML: schema.value,
  }))

  const eventAttendanceModeOptions = {
    HYBRID: 'https://schema.org/MixedEventAttendanceMode',
    IN_PERSON: 'https://schema.org/OfflineEventAttendanceMode',
    ONLINE: 'https://schema.org/OnlineEventAttendanceMode',
  }

  const fullLocalizedUrl = useFullLocalizedUrl()
  const {
    public: { baseUrl },
  } = useRuntimeConfig()

  const createSchema = (): Event[] => {
    function formatStartDate(startDate: number | undefined, hasDates: boolean) {
      if (!startDate) return
      dayjs.extend(utc)
      dayjs.extend(timezone)

      return hasDates
        ? dayjs(startDate * 1000)
            .tz('Europe/Berlin')
            .toISOString()
        : dayjs().tz('Europe/Berlin').add(1, 'month').toISOString()
    }

    return events.map((event) => {
      const hasDates = Boolean(
        searchEngineEventGetters.fnGetNextDateAvailableStart(event)
      )

      const supplierUrl =
        searchEngineEventGetters.getSupplierPermalink(event) &&
        fullLocalizedUrl(
          `/p/${searchEngineEventGetters.getSupplierPermalink(event)}/`
        )

      const obj: WithContext<Event> = {
        '@type': 'Event',
        name: searchEngineEventGetters.fnGetTitle(event),
        url: fullLocalizedUrl(
          `/e/${searchEngineEventGetters.fnGetPermalink(event)}/`
        ),
        startDate: formatStartDate(
          searchEngineEventGetters.fnGetNextDateAvailableStart(event),
          hasDates
        ),
        endDate: formatStartDate(
          searchEngineEventGetters.fnGetNextDateAvailableEnd(event),
          hasDates
        ),
        eventAttendanceMode:
          eventAttendanceModeOptions[
            searchEngineEventGetters.fnGetType(event)
          ] || '',
        // TODO dynamic eventStatus
        eventStatus: 'https://schema.org/EventScheduled',
        image: [searchEngineEventGetters.getPrincipalImage(event)],
        description: searchEngineEventGetters.fnGetSubtitle(event),
        offers: {
          '@type': 'Offer',
          availability: 'InStock',
          price: searchEngineEventGetters.fnGetMinimumPriceAmount(event) / 100,
          url: fullLocalizedUrl(
            `/e/${searchEngineEventGetters.fnGetPermalink(event)}/`
          ),
          priceCurrency:
            searchEngineEventGetters.fnGetMinimumPriceCurrency(event),
        },
        performer: {
          '@type': 'PerformingGroup',
          name: searchEngineEventGetters.fnGetSupplier(event),
          url: supplierUrl,
        },
        organizer: {
          '@type': 'Organization',
          name: searchEngineEventGetters.fnGetSupplier(event),
          url: supplierUrl,
        },

        ...(searchEngineEventGetters.getAverageRating(event) &&
          searchEngineEventGetters.getRatingsAmount(event) > 0 && {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: searchEngineEventGetters.getAverageRating(
                event,
                'en-US'
              ),
              bestRating: 5,
              worstRating: 1,
              ratingCount: searchEngineEventGetters.getRatingsAmount(event),
            },
          }),
      }

      if (searchEngineEventGetters.fnGetType(event) !== 'ONLINE') {
        obj.location = {
          '@type': 'Place',
          name:
            overwrites?.locality?.name ||
            searchEngineEventGetters.fnGetCitySlug(event),
          address: {
            '@type': 'PostalAddress',
            name: searchEngineEventGetters.fnGetFormattedAddress(event),
            postalCode: searchEngineEventGetters.fnGetFormattedZipcode(event),
          },
        }
      } else {
        obj.location = {
          '@type': 'VirtualLocation',
          url: baseUrl,
        }
      }

      return obj as WithContext<Event>
    })
  }

  schema.value = { '@context': 'https://schema.org', '@graph': createSchema() }

  return {
    schema,
    script,
  }
}

export default useSearchEngineEventSchema
